/**
 * i18n - 다국어 번역 데이터
 * 영어 (English) 및 한국어 (한국어) 지원
 */

export const translations = {
  en: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      alerts: 'Alerts',
      policies: 'Policies',
      events: 'Events',
      reports: 'Reports',
      sentinelDescription: 'Sentinel is an advanced runtime security platform powered by KubeArmor that provides comprehensive protection for Kubernetes clusters. It offers real-time threat detection and policy enforcement for process execution, file access, and network connections. Key features include: (1) Runtime Security Monitoring - Real-time tracking of all system activities and events in your cluster; (2) Policy-based Access Control - Define and enforce fine-grained security policies for workloads; (3) Threat Detection & Response - Detect unauthorized activities and security incidents with automatic alerts; (4) Compliance Management - Track regulatory compliance against industry standards; (5) Agent-based Protection - Deploy lightweight agents on every node for comprehensive coverage; (6) Multi-framework Support - Works across different Kubernetes distributions and configurations. This dashboard provides visibility into all these capabilities with actionable insights for security teams to maintain robust cluster security posture.',
    },

    // Dashboard Header
    dashboard: {
      title: '🛡️ KubeArmor Security Dashboard',
      lastUpdated: 'Last updated: ',
    },

    // Core Metrics Section
    coreMetrics: {
      title: '📊 Core Metrics',
      description: '클러스터의 실시간 보안 상태를 한눈에 파악할 수 있는 핵심 지표입니다. 총 이벤트 수, 허용/차단 비율, 활성 정책 수, 긴급 알림, 사용 가능한 노드 등을 표시합니다. 이 지표들은 전체 보안 태세 평가의 기초이며, 정기적으로 모니터링하여 추세를 파악하는 것이 중요합니다.',
      totalEvents: 'Total Events',
      allowedEvents: 'Allowed Events',
      blockedEvents: 'Blocked Events',
      activePolicies: 'Active Policies',
      criticalAlerts: 'Critical Alerts',
      securityScore: 'Security Score',
      complianceRate: 'Compliance Rate',
      protectedNodes: 'Protected Nodes',
      today: 'today',
      immediateAttention: 'Immediate attention',
      regulatory: 'Regulatory compliant',
      total: 'total',
      of: 'of',
    },

    // Event Analysis Section
    eventAnalysis: {
      title: '🔍 Event Analysis (24H)',
      description: '지난 24시간 동안 발생한 모든 보안 이벤트를 유형과 심각도별로 분류하여 시각화합니다. 프로세스 실행, 파일 접근, 네트워크 연결 등의 이벤트를 시간대별로 추적할 수 있습니다. 비정상적인 패턴이나 증가 추세를 빠르게 발견하고 대응하는 데 매우 중요합니다.',
      detailTitle: '📊 Event Analysis',
      detailDescription: 'Detailed event records and analysis',
      eventsByType: 'Events by Type',
      severityDistribution: 'Severity Distribution',
      timeline24h: '24-Hour Event Timeline',
      columns: {
        timestamp: 'Timestamp',
        type: 'Type',
        source: 'Source',
        pod: 'Pod',
        action: 'Action',
        severity: 'Severity',
      },
      filters: {
        eventType: 'Event Type',
        severityLevel: 'Severity Level',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Policy Analysis Section
    policyAnalysis: {
      title: '📋 Policy Analysis',
      description: '쿠버네티스 보안 정책의 전체 구성과 효율성을 평가합니다. 정책 유형별 분포, 활성/비활성 상태, 각 정책의 효과성 메트릭을 보여줍니다. 효과적의 정책과 적용되지 않는 정책을 파악하여 정책 최적화 결정을 내릴 수 있습니다.',
      policiesByType: 'Policies by Type',
      policyStatus: 'Policy Status',
      topPoliciesEffectiveness: 'Top Policies by Effectiveness',
      columns: {
        policyName: 'Policy Name',
        type: 'Type',
        status: 'Status',
        effectiveness: 'Effectiveness',
        targetPods: 'Target Pods',
        violations: 'Violations',
      },
      filters: {
        policyType: 'Policy Type',
        status: 'Status',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Security Controls Section
    securityControls: {
      title: '🔐 Security Controls',
      description: '런타임에서 실제로 실행되는 보안 제어의 효과를 실시간으로 추적합니다. 프로세스 실행, 파일 접근, 네트워크 통신 각각에서 허용/차단된 작업의 비율을 표시합니다. 제어가 제대로 작동하는지 검증하고 정책 조정의 필요성을 판단하는 데 필수적입니다.',
      processExecution: 'Process Execution Control',
      fileAccess: 'File Access Control',
      networkControl: 'Network Control',
      allowed: 'Allowed',
      blocked: 'Blocked',
      inbound: 'Inbound',
      outbound: 'Outbound',
      columns: {
        controlName: 'Control Name',
        type: 'Type',
        status: 'Status',
        allowed: 'Allowed',
        blocked: 'Blocked',
        effectiveness: 'Effectiveness',
      },
      filters: {
        controlType: 'Control Type',
        status: 'Status',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Policy Violations Section
    policyViolations: {
      title: '📋 Policy Violations',
      description: '정책에 위배되는 미승인 활동과 보안 사건을 추적합니다. 위반 유형, 발생 위치, 심각도별로 사건을 분류하여 빠른 대응을 가능하게 합니다. 반복적인 위반 패턴을 파악하면 정책 개선과 사용자 교육이 필요한 영역을 알 수 있습니다.',
      detailTitle: '🚨 Policy Violations Analysis',
      detailDescription: 'Detailed view of policy violations and unauthorized activities',
      columns: {
        violatedPolicy: 'Violated Policy',
        violationType: 'Violation Type',
        source: 'Source (Process/User)',
        pod: 'Pod',
        severity: 'Severity',
        status: 'Status',
      },
      filters: {
        violationType: 'Violation Type',
        severity: 'Severity',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Threats Section
    threats: {
      title: '⚠️ Top Threats & Attack Patterns',
      description: '클러스터에서 감지된 주요 위협들을 발생 빈도와 심각도로 순위를 매겨 표시합니다. 반복되는 공격 패턴을 식별하고 우선순위를 정하여 대응합니다. 이를 통해 가장 영향력 있는 위협에 집중할 수 있고 리소스를 효율적으로 배분할 수 있습니다.',
      detailTitle: '🚨 Threat Analysis',
      detailDescription: 'Detected threats and security incidents',
      rank: 'Rank',
      threat: 'Threat',
      events: 'events',
      action: 'Action',
      policy: 'Policy',
      namespace: 'Namespace',
      pod: 'Pod',
      columns: {
        timestamp: 'Timestamp',
        threatName: 'Threat Name',
        type: 'Type',
        severity: 'Severity',
        count: 'Count',
        lastSeen: 'Last Seen',
        status: 'Status',
      },
      filters: {
        threatType: 'Threat Type',
        severityLevel: 'Severity Level',
        status: 'Status',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Node & Host Security
    nodeHost: {
      title: '🖥️ Node & Host Security',
      description: '클러스터의 각 노드별 보안 상태와 보호 현황을 모니터링합니다. 보호된 노드 수, 적용된 정책 수, 24시간 이벤트, 리소스 사용률을 표시합니다. 노드별 차이를 파악하여 보호 수준의 일관성을 유지하고 문제 노드를 빠르게 식별합니다.',
      detailTitle: '🔧 Node Analysis',
      detailDescription: 'Protected nodes and security status overview',
      nodeName: 'Node',
      status: 'Status',
      protection: 'Protection',
      policies: 'Policies',
      events24h: 'Events (24h)',
      blocked: 'Blocked',
      healthy: 'Healthy',
      columns: {
        nodeName: 'Node Name',
        ip: 'IP Address',
        threats: 'Threats',
        protection: 'Protection',
        policies: 'Policies',
        resources: 'CPU / Memory',
      },
      filters: {
        namespace: 'Namespace',
        status: 'Status',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Compliance & Audit
    compliance: {
      title: '✅ Compliance & Audit',
      description: '업계 표준 규정 준수 현황을 프레임워크별로 추적합니다. 각 규정 요구사항에 대한 현재 준수 상태를 점수로 표시하고 감사 이벤트를 기록합니다. 규정 준수 관리자와 감사인을 위한 필수 보고서와 증거 수집을 지원합니다.',
      detailTitle: '📋 Compliance Analysis',
      detailDescription: 'Regulatory compliance framework details and control status',
      complianceScore: 'Compliance Score by Framework',
      recentAuditEvents: 'Recent Audit Events',
      timestamp: 'Timestamp',
      event: 'Event',
      user: 'User',
      columns: {
        framework: 'Framework',
        compliance: 'Compliance %',
        controls: 'Controls',
        status: 'Status',
        severity: 'Severity',
        lastAudit: 'Last Audit',
      },
      filters: {
        framework: 'Framework',
        status: 'Status',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Agent Status
    agentStatus: {
      title: '🤖 KubeArmor Agent Status',
      description: 'KubeArmor 에이전트의 배포 상태와 건강도를 실시간 모니터링합니다. 실행 중/오류/중지된 에이전트 수, 버전 불일치 감지 등을 표시합니다. 에이전트 문제를 빠르게 발견하여 보안 보호 공백을 방지합니다.',
      detailTitle: '🤖 KubeArmor Agent Status',
      detailDescription: 'Monitor KubeArmor agent health, version consistency, and deployment status',
      totalAgents: 'Total Agents',
      running: 'Running',
      error: 'Error',
      stopped: 'Stopped',
      total: 'Total',
      versionMismatchWarning: 'Version mismatch detected on 1 node - Click to view details',
      versionMismatchTitle: 'Version Mismatch Details',
      versionMismatchDescription: 'The following nodes have version mismatches between agent and KubeArmor. Please update to maintain consistency.',
      columns: {
        nodeName: 'Node Name',
        nodeIP: 'Node IP',
        agentStatus: 'Agent Status',
        agentVersion: 'Agent Version',
        lastHeartbeat: 'Last Heartbeat',
        resourceUsage: 'Resource Usage',
      },
      filters: {
        status: 'Agent Status',
        version: 'Agent Version',
        all: 'All',
        searchResults: 'Search Results',
      },
    },

    // Cluster Health
    clusterHealth: {
      title: '⚡ Cluster Health & Performance',
      description: 'CPU 사용률, 메모리 소비, 이벤트 처리 지연, 네트워크 성능 등 시스템 건강도 지표를 모니터링합니다. 클러스터 전체의 안정성을 평가하고 리소스 병목 현상을 식별하여 성능 최적화 결정을 내릴 수 있습니다.',
      healthStatus: 'Cluster Health Status',
      performanceMetrics: 'Performance Metrics',
      eventProcessingLatency: 'Event Processing Latency',
      resourceUsage: 'Resource Usage',
      cpu: 'CPU',
      memory: 'Memory',
    },

    // Weekly Trends
    weeklyTrends: {
      title: '📈 Weekly Trends',
      description: '지난 한 주간의 보안 이벤트와 정책 위반 추세를 시각화합니다. 요일별 패턴, 시간대별 활동 변화를 파악하여 반복적인 문제나 신규 위협을 조기에 발견합니다. 장기 전략 수립과 예측 기반 대응에 활용됩니다.',
    },

    // Threat Intelligence
    threatIntel: {
      title: '🔬 Threat Intelligence',
      description: 'MITRE ATT&CK 프레임워크를 기반으로 클러스터에서 감지된 공격 기법을 추적합니다. 어떤 공격 기법이 탐지되고 차단되었는지 시각화하여 위협 인텔리전스와 산업 위협 지형을 이해할 수 있습니다.',
      mitreCoverage: 'MITRE ATT&CK Coverage',
      recentDetections: 'Recent Detections',
      detected: 'DETECTED',
      blocked: 'BLOCKED',
    },

    // Resource Security
    resourceSecurity: {
      title: '📦 Resource Security Status',
      description: '쿠버네티스 리소스(Deployment, Pod, Service 등)의 보안 상태를 모니터링합니다. 보호되는 Pod 수, 네임스페이스별 커버리지, 위협 수준 평가를 표시합니다. 리소스 단위의 보안 상태를 파악하여 취약한 영역을 강화할 수 있습니다.',
      resource: 'Resource',
      type: 'Type',
      namespace: 'Namespace',
      protectedPods: 'Protected Pods',
      threatLevel: 'Threat Level',
    },

    // Recommendations
    recommendations: {
      title: '💡 Policy Recommendations',
      description: '머신러닝 기반 분석으로 감지된 위협과 사용 패턴을 바탕으로 보안 정책 개선안을 제시합니다. 현재 정책의 강점과 개선 기회, 누락된 보호 영역 등을 파악하여 정책 최적화 의사결정을 지원합니다.',
    },

    // Footer
    footer: {
      description: 'Advanced Security Monitoring & Policy Management for Kubernetes',
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      dashboard: 'Dashboard',
      events: 'Events',
      policies: 'Policies',
      accuknox: 'AccuKnox',
      kubearmor: 'KubeArmor',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      license: 'License',
      copyright: '© 2026 kim seok joon. All rights reserved.',
      version: 'Sentinel v1.0.0',
    },

    // Common
    common: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      active: 'Active',
      inactive: 'Inactive',
      testing: 'Testing',
      type: 'Type',
      status: 'Status',
    },
  },

  ko: {
    // Navigation
    nav: {
      dashboard: '대시보드',
      alerts: '알림',
      policies: '정책',
      events: '이벤트',
      reports: '보고서',
      sentinelDescription: 'Sentinel은 KubeArmor로 구동되는 고급 런타임 보안 플랫폼으로, 쿠버네티스 클러스터를 위한 포괄적인 보호를 제공합니다. 프로세스 실행, 파일 접근, 네트워크 연결에 대한 실시간 위협 탐지 및 정책 집행을 제공합니다. 주요 기능: (1) 런타임 보안 모니터링 - 클러스터의 모든 시스템 활동과 이벤트를 실시간으로 추적; (2) 정책 기반 접근 제어 - 워크로드에 대한 세밀한 보안 정책 정의 및 시행; (3) 위협 탐지 및 대응 - 미승인 활동과 보안 사건을 감지하고 자동 알림; (4) 규정 준수 관리 - 업계 표준 규정에 대한 준수 상태 추적; (5) 에이전트 기반 보호 - 모든 노드에 경량 에이전트 배포로 포괄적 커버리지 제공; (6) 다중 프레임워크 지원 - 다양한 쿠버네티스 배포판 및 구성에서 동작. 이 대시보드는 모든 기능에 대한 가시성을 제공하고 보안 팀이 강력한 클러스터 보안 태세를 유지하도록 지원합니다.',
    },

    // Dashboard Header
    dashboard: {
      title: '🛡️ KubeArmor 보안 대시보드',
      lastUpdated: '마지막 업데이트: ',
    },

    // Core Metrics Section
    coreMetrics: {
      title: '📊 핵심 지표',
      description: '실시간 보안 상태를 나타내는 주요 성능 지표. 단축 사건, 차단된 동작, 활성 정책, 치명적 알림, 보안 점수, 규정 준수율, 보호된 노드 등을 표시합니다.',
      totalEvents: '전체 이벤트',
      allowedEvents: '허용된 이벤트',
      blockedEvents: '차단된 이벤트',
      activePolicies: '활성 정책',
      criticalAlerts: '치명적 알림',
      securityScore: '보안 점수',
      complianceRate: '준수율',
      protectedNodes: '보호된 노드',
      today: '오늘',
      immediateAttention: '즉시 주의 필요',
      regulatory: '규제 준수',
      total: '전체',
      of: '/',
    },

    // Event Analysis Section
    eventAnalysis: {
      title: '🔍 이벤트 분석 (24시간)',
      description: '실시간 이벤트 추적으로 유형 및 심각도별 분류. 24시간 시스템 활동 타임라인을 나타냅니다.',
      detailTitle: '📊 이벤트 분석',
      detailDescription: '상세 이벤트 기록 및 분석',
      eventsByType: '유형별 이벤트',
      severityDistribution: '심각도 분포',
      timeline24h: '24시간 이벤트 타임라인',
      columns: {
        timestamp: '시간',
        type: '타입',        source: '원천',        pod: 'Pod',
        action: '액션',
        severity: '심각도',
      },
      filters: {
        eventType: '이벤트 타입',
        severityLevel: '심각도',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Policy Analysis Section
    policyAnalysis: {
      title: '📋 정책 분석',
      description: '보안 정책 관리 및 평가. 정책 유형, 상태, 효율성 메트릭 시각화. 나머지 먼저 정책 및 커버리지 단답.',
      policiesByType: '유형별 정책',
      policyStatus: '정책 상태',
      topPoliciesEffectiveness: '효과성이 높은 상위 정책',
      columns: {
        policyName: '정책명',
        type: '타입',
        status: '상태',
        effectiveness: '효과성',
        targetPods: '대상Pod',
        violations: '위반',
      },
      filters: {
        policyType: '정책 유형',
        status: '상태',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Security Controls Section
    securityControls: {
      title: '🔐 보안 제어',
      description: '런타임 보안 제어 실행 상태. 프로세스/파일/네트워크 동작의 허용/차단 연연을 나타냅니다.',
      processExecution: '프로세스 실행 제어',
      fileAccess: '파일 접근 제어',
      networkControl: '네트워크 제어',
      allowed: '허용됨',
      blocked: '차단됨',
      inbound: '인바운드',
      outbound: '아웃바운드',
      columns: {
        controlName: '제어 이름',
        type: '타입',
        status: '상태',
        allowed: '허용됨',
        blocked: '차단됨',
        effectiveness: '효율성',
      },
      filters: {
        controlType: '제어 타입',
        status: '상태',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Policy Violations Section
    policyViolations: {
      title: '📋 정책 위반',
      description: '미의 활동 및 정책 위반 기록. 유형, 원천, 심각도별 위반 추적. 빠른 식별 및 대응 활성화.',
      detailTitle: '🚨 정책 위반 분석',
      detailDescription: '정책 위반 및 승인되지 않은 활동의 상세 보기',
      columns: {
        violatedPolicy: '위반된 정책',
        violationType: '위반 유형',
        source: '원천 (프로세스/사용자)',
        pod: '파드',
        severity: '심각도',
        status: '상태',
      },
      filters: {
        violationType: '위반 유형',
        severity: '심각도',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Threats Section
    threats: {
      title: '⚠️ 상위 위협 및 공격 패턴',      description: '감지된 상위 보안 위협 및 공격 패턴. 빈도와 심각도로 순위를 매긴 위협을 식별합니다.',      detailTitle: '🚨 위협 분석',
      detailDescription: '탐지된 위협 및 보안 인시던트',
      rank: '순위',
      threat: '위협',
      events: '이벤트',
      action: '조치',
      policy: '정책',
      namespace: '네임스페이스',
      pod: '파드',
      columns: {
        timestamp: '시간',
        threatName: '위협명',
        type: '유형',
        severity: '심각도',
        count: '감지수',
        lastSeen: '마지막 감지',
        status: '상태',
      },
      filters: {
        threatType: '위협 타입',
        severityLevel: '심각도',
        status: '상태',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Node & Host Security
    nodeHost: {
      title: '🖥️ 노드 및 호스트 보안',      description: '클러스터 노드 보안 상태 및 보호 상태. 보호된 노드, 노드당 활성 정책, 위협 수, 리소스 활용률 표시.',      detailTitle: '🔧 노드 분석',
      detailDescription: '보호된 노드 및 보안 상태 개요',
      nodeName: '노드',
      status: '상태',
      protection: '보호 수준',
      policies: '정책',
      events24h: '이벤트 (24시간)',
      blocked: '차단됨',
      healthy: '정상',
      columns: {
        nodeName: '노드명',
        ip: 'IP 주소',
        threats: '위협',
        protection: '보호',
        policies: '정책',
        resources: 'CPU / Memory',
      },
      filters: {
        namespace: '네임스페이스',
        status: '상태',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Compliance & Audit
    compliance: {
      title: '✅ 준수 및 감사',      description: '업계 표준 대비 규정 준수 상태. 프레임워크별 준수 점수 및 감사 이벤트 추적.',      detailTitle: '📋 규정 준수 분석',
      detailDescription: '규제 준수 프레임워크 상세 정보 및 제어 상태',
      complianceScore: '프레임워크별 준수 점수',
      recentAuditEvents: '최근 감사 이벤트',
      timestamp: '타임스탬프',
      event: '이벤트',
      user: '사용자',      columns: {
        framework: '프레임워크',
        compliance: '준수율 %',
        controls: '제어',
        status: '상태',
        severity: '심각도',
        lastAudit: '마지막 감사',
      },
      filters: {
        framework: '프레임워크',
        status: '상태',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Agent Status
    agentStatus: {
      title: '🤖 KubeArmor 에이전트 상태',
      description: '실시간 KubeArmor 에이전트 상태 모니터링. 실행/오류/중지된 에이전트 추적 및 버전 불일치 감지.',
      detailTitle: '🤖 KubeArmor 에이전트 상태',
      detailDescription: 'KubeArmor 에이전트 상태, 버전 일관성 및 배포 상태 모니터링',
      totalAgents: '전체 에이전트',
      running: '실행 중',
      error: '오류',
      stopped: '중지됨',
      total: '전체',
      versionMismatchWarning: '1개 노드에서 버전 불일치 감지 - 클릭하여 자세히 보기',
      versionMismatchTitle: '버전 불일치 상세 정보',
      versionMismatchDescription: '다음 노드들은 에이전트와 KubeArmor 버전 간의 불일치가 있습니다. 일관성을 유지하기 위해 업데이트하세요.',
      columns: {
        nodeName: '노드명',
        nodeIP: '노드 IP',
        agentStatus: '에이전트 상태',
        agentVersion: '에이전트 버전',
        lastHeartbeat: '마지막 하트비트',
        resourceUsage: '리소스 사용량',
      },
      filters: {
        status: '에이전트 상태',
        version: '에이전트 버전',
        all: '전체',
        searchResults: '검색 결과',
      },
    },

    // Cluster Health
    clusterHealth: {
      title: '⚡ 클러스터 건강도 및 성능',
      description: 'CPU 사용량, 메모리 소비, 이벤트 처리 지연 시간, 네트워크 성능 포함 시스템 상태 메트릭.',
      healthStatus: '클러스터 건강도',
      performanceMetrics: '성능 메트릭',
      eventProcessingLatency: '이벤트 처리 지연시간',
      resourceUsage: '리소스 사용량',
      cpu: 'CPU',
      memory: '메모리',
    },

    // Weekly Trends
    weeklyTrends: {
      title: '📈 주간 추세',
      description: '지난 주간의 보안 이벤트 및 정책 위반 역사 분석. 패턴 및 추세 식별로 전략적 보안 계획 수립.',
    },

    // Threat Intelligence
    threatIntel: {
      title: '🔬 위협 인텔리전스',
      description: 'MITRE ATT&CK 프레임워크 커버리지 분석 및 최근 위협 감지. 클러스터에서 감지 및 차단된 공격 기술 표시.',
      mitreCoverage: 'MITRE ATT&CK 커버리지',
      recentDetections: '최근 탐지',
      detected: '탐지됨',
      blocked: '차단됨',
    },

    // Resource Security
    resourceSecurity: {
      title: '📦 리소스 보안 상태',
      description: '쿠버네티스 리소스(배포, 파드, 서비스) 보안 상태. 보호된 파드, 네임스페이스, 위협 수준 평가 표시.',
      resource: '리소스',
      type: '유형',
      namespace: '네임스페이스',
      protectedPods: '보호된 파드',
      threatLevel: '위협 수준',
    },

    // Recommendations
    recommendations: {
      title: '💡 정책 권장사항',
      description: '감지된 위협 및 사용 패턴 기반 AI 보안 정책 권장사항. 제안된 정책 개선으로 보안 상태 최적화.',
    },

    // Footer
    footer: {
      description: '고급 보안 모니터링 및 쿠버네티스 정책 관리',
      product: '상품',
      company: '회사',
      legal: '법률',
      dashboard: '대시보드',
      events: '이벤트',
      policies: '정책',
      accuknox: 'AccuKnox',
      kubearmor: 'KubeArmor',
      privacy: '개인정보처리방침',
      terms: '이용약관',
      license: '라이선스',
      copyright: '© 2026 kim seok joon. 모든 권리 보유.',
      version: 'Sentinel v1.0.0',
    },

    // Common
    common: {
      critical: '치명적',
      high: '높음',
      medium: '중간',
      low: '낮음',
      active: '활성',
      inactive: '비활성',
      testing: '테스트 중',
      type: '유형',
      status: '상태',
    },
  },
};

export type Language = 'en' | 'ko';
export type TranslationKeys = typeof translations.en;
